/**
 * ForgotPasswordPage Component
 * 
 * Allows users to request a password reset link via email.
 * Implements security best practice by not revealing if email exists.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onBack - Callback to go back to previous screen
 * @param {Function} props.onSuccess - Callback when process completes
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function ForgotPasswordPage({ onBack, onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/forgot-password`, { 
        email,
        frontend_url: window.location.origin  // Pass current frontend URL for email links
      });
      setSubmitted(true);
      toast.success('If the email exists, a password reset link has been sent');
    } catch (error) {
      console.error('Forgot password request failed:', error);
      // Always show success message to prevent email enumeration
      setSubmitted(true);
      toast.success('If the email exists, a password reset link has been sent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center relative">
            <button 
              onClick={onBack}
              className="absolute left-0 top-0 text-orange-600 hover:text-orange-700 flex items-center gap-1 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_bawarchi-catering-2/artifacts/6e6yv8t6_caterlog-high-resolution-logo-transparent.png" 
              alt="CaterLog" 
              className="h-14 mx-auto mb-4"
            />
            <CardTitle className="text-2xl mb-2">Forgot Password</CardTitle>
            <p className="text-sm text-muted">Enter your email to receive a password reset link</p>
          </div>
        </CardHeader>
        <CardContent>
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-bawarchi">Email Address *</label>
                <input
                  type="email"
                  className="input-bawarchi"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={loading}
                  autoFocus
                />
              </div>
              
              <button
                type="submit"
                className="btn-bawarchi-primary w-full"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              
              <div className="text-center text-sm text-gray-600">
                <p>Remember your password? <button type="button" onClick={onBack} className="text-orange-600 hover:text-orange-700 font-medium">Sign in</button></p>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600 mb-4">
                  If an account exists for {email}, you will receive a password reset link shortly.
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  The link will expire in 60 minutes.
                </p>
              </div>
              <button
                onClick={onBack}
                className="btn-bawarchi-secondary w-full"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPasswordPage;